import { FormikProps } from 'formik';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionButtonGroup, Responsive, SettingsButtons, TabContainer } from '@components';
import { AppConfigurationTab, FormActionType, FormType, Translation } from '@enums';
import { useFormHandlers, useFormLoadingStatus, useFormProcedure, useFormSubmissionStatus } from '@hooks';
import { getAppConfigurationTabs, validateValueEqual } from '@utils';

import { AppConfigurationForm } from './AppConfigurationForm';

export const AppConfiguration = () => {
  const formType = FormType.CdnApplicationConfiguration;

  const { t: tTabs } = useTranslation(Translation.Configuration, { keyPrefix: 'tabs.appConfiguration' });
  const { t: tConfigurationButton } = useTranslation(Translation.Configuration, { keyPrefix: 'buttons' });

  const formikRef = useRef<FormikProps<any> | null>(null);
  const [activeTab, setActiveTab] = useState<AppConfigurationTab>(AppConfigurationTab.CONFIGURATION);

  const formProcedure = useFormProcedure(formType);
  const formSubmitting = useFormSubmissionStatus({ formType });
  const formLoading = useFormLoadingStatus({ formType });

  useEffect(() => {
    if (formikRef.current?.errors && Object.keys(formikRef.current.errors).length > 0) {
      formikRef.current.validateForm();
    }
  }, [tTabs]);

  const handleSaveSettings = useCallback(() => {
    formikRef?.current?.handleSubmit();
  }, []);

  const handleFormikInstance = useCallback((formik: FormikProps<any> | null) => {
    formikRef.current = formik;
  }, []);

  const AppConfigurationFormHandler = useFormHandlers({
    formType,
    handlerOptions: [
      {
        type: FormActionType.Save,
        function: handleSaveSettings,
        label: tConfigurationButton('saveSettings'),
        loading: formSubmitting,
        disabled: formLoading,
      },
    ],
  });

  const contentConfig = useCallback(() => {
    return {
      [AppConfigurationTab.CONFIGURATION]: <AppConfigurationForm onFormikInstance={handleFormikInstance} />,
    };
  }, []);

  const ConfigurationTabs = useMemo(() => getAppConfigurationTabs(), [tTabs]);

  return (
    <div className="w-full flex flex-col flex-1 gap-6 sm:gap-8">
      <TabContainer<AppConfigurationTab>
        tabs={ConfigurationTabs}
        onTabChange={setActiveTab}
        contentConfig={contentConfig()}
        contentClassName="gap-6 sm:gap-8"
        adornment={
          <Responsive showAbove="sm">
            {validateValueEqual(activeTab, AppConfigurationTab.CONFIGURATION) && (
              <ActionButtonGroup actionHandlers={AppConfigurationFormHandler} procedure={formProcedure} />
            )}
          </Responsive>
        }
      />
      <Responsive showBelow="sm">
        {validateValueEqual(activeTab, AppConfigurationTab.CONFIGURATION) && (
          <SettingsButtons handleSaveModal={handleSaveSettings} formType={formType} />
        )}
      </Responsive>
    </div>
  );
};
